import React from 'react';
import { motion } from 'framer-motion';
import { User, Eye, TrendingUp, Target } from 'lucide-react';
export function IndividualLevelPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-513" data-mp="2wixjbrnv6hpx">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-514" data-mp="2rkmclpsv8nbx">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-515" data-mp="2mm1atlxytvld">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-516" data-mp="28whaemruxaf8">
            <span className="w-8 h-px bg-teal" data-id="element-517" data-mp="23wcv302oqlql"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-518" data-mp="23xw8miwyiioo">
              Scale
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-519" data-mp="1yz2v4qho6roi"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-520" data-mp="1uw6jxkuglxul">
            Individual Level
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-521" data-mp="5ew2zo2bytno">
            At the individual level, NCJI is designed as a framework for human
            development, self-awareness, and opportunity guidance. It helps
            people better understand how they think, where their strengths are,
            where their bottlenecks lie, and what capabilities are most
            important to strengthen in order to improve long-term outcomes.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-522" data-mp="acxnmdfen119">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-523" data-mp="ee114t9zckzb">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-524" data-mp="15l9b54jc6ag2">
            The purpose of NCJI at this level is not simply to classify a
            person. It is to create a clearer picture of readiness and
            development. It translates capability gaps into growth pathways,
            identifies where support structures may be needed, and helps connect
            individuals to more realistic routes for progress across education,
            work, civic participation, and leadership over time.
          </p>
        </motion.div>
      </section>

      {/* Self-Awareness and Capability Insight */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-525" data-mp="cy5cdhau6scq">
        <div className="max-w-4xl mx-auto" data-id="element-526" data-mp="80d9js8nlltr">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-527" data-mp="27bo58dtx9mrp">
            <div className="flex items-center gap-4 mb-6" data-id="element-528" data-mp="2eu4dpmq5hq6j">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-529" data-mp="2jmfc2l58t3wz">
                <Eye size={24} data-id="element-530" data-mp="2olryx5w46azk" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-531" data-mp="2okh0lllftvfu">
                Self-Awareness and Capability Insight
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-532" data-mp="1vxmekk8z2o09">
              NCJI helps individuals see more clearly how they reason, how they
              respond to pressure, how well they plan, how vulnerable they may
              be to manipulation, and where they may need stronger emotional
              regulation, execution, or social capability. This creates a more
              grounded form of self-awareness, one tied not just to identity or
              self-perception, but to practical readiness and development.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Development, Not Just Diagnosis */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-533" data-mp="p4qapz8175lg">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-534" data-mp="133g6nbj46z88">
          <div className="flex items-center gap-4 mb-6" data-id="element-535" data-mp="17z58xmmiqu88">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-536" data-mp="1lrl4k2d3bpz4">
              <TrendingUp size={24} data-id="element-537" data-mp="1hs427hsyn8y6" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-538" data-mp="1vqtxqt04t0kf">
              Development, Not Just Diagnosis
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-539" data-mp="1qt28k6qvfsjx">
            A core principle of NCJI is that every meaningful weakness should be
            paired with a pathway. At the individual level, this means the
            system should not stop at showing where someone is underdeveloped.
            It should also help clarify what stronger readiness would require
            and what kinds of support, tools, or experiences could help build
            it.
          </p>
        </motion.div>
      </section>

      {/* Opportunity Guidance */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-540" data-mp="1wdhvld6ao2aa">
        <div className="max-w-4xl mx-auto" data-id="element-541" data-mp="1hovgnckkxcjl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-542" data-mp="1hpwutpww6f5j">
            <div className="flex items-center gap-4 mb-6" data-id="element-543" data-mp="1crlin8j4jsk5">
              <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-544" data-mp="3fd7ll323cnns">
                <Target size={24} data-id="element-545" data-mp="3ak4tkzcypm9p" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-546" data-mp="35m37e5k8p6u0">
                Opportunity Guidance
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-547" data-mp="30pcs3kg9ef9n">
              Many people have real strengths that remain underdeveloped because
              they are not connected to the right opportunities, environments,
              or support systems. NCJI helps identify where opportunity
              alignment is weak and where a person may benefit from mentorship,
              training, structured experience, education, or broader access
              pathways. This makes the framework useful not only for
              self-understanding, but for long-term mobility and contribution.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Progress Over Time */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-548" data-mp="og5uwnwywej3">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-549" data-mp="sgptzx13ps7v">
          <div className="flex items-center gap-4 mb-6" data-id="element-550" data-mp="174av5lef1hd5">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-551" data-mp="1c3e1d964ttmp">
              <User size={24} data-id="element-552" data-mp="1g593ugzsud22" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-553" data-mp="1g3ef0rleq0sp">
              Progress Over Time
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-554" data-mp="3hxdnz6b24628">
            NCJI is designed to support movement, not a fixed label. At the
            individual level, that means helping people reassess, improve, and
            grow over time as capabilities strengthen and readiness expands. The
            long-term goal is to help individuals move from potential to greater
            clarity, stronger judgment, and broader responsibility.
          </p>
        </motion.div>
      </section>
    </div>;
}